import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FaSyncAlt, FaHistory, FaCalendarAlt, FaExclamationTriangle,
  FaCheckCircle, FaTimesCircle, FaPlay, FaRedo, FaFilter,
  FaListUl, FaPause, FaBan, FaEdit, FaChevronDown, FaChevronUp,
} from "react-icons/fa";
import api from "./apiClient";
import { useTheme, getT, glassCard, getBrandColors } from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";

const glassCardSt = (T) => ({ background: T.glass, backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)", border: T.glassBdr, borderRadius: 16, boxShadow: T.glassSh, padding: "20px 22px", boxSizing: "border-box" });
const tabBtn = (T, active) => ({
  padding: "9px 16px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer",
  border: active ? "none" : `1px solid ${T.inputBdr}`,
  background: active ? `linear-gradient(135deg,${T.yellow},#fff3a0)` : T.glass,
  color: active ? T.blueDark : T.textLabel,
  display: "flex", alignItems: "center", gap: 7,
});
const selSt = (T) => ({ padding: "7px 10px", borderRadius: 8, border: `1px solid ${T.inputBdr}`, background: T.inputBg, color: T.inputText, fontSize: 12.5 });
const tile = (T) => ({ ...glassCardSt(T), flex: "1 1 160px", minWidth: 160 });
const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const CATEGORY_LABEL = { student: "Student Billing", lane_membership: "Lane Membership" };
const STATUS_COLOR = (T, s) => {
  if (s === "success") return T.green;
  if (s === "failed") return T.red;
  return T.orange || T.gray;
};
const SCHED_STATUS_COLOR = (T, s) => ({
  active: T.green, paused: T.orange, past_due: T.red, cancelled: T.textMuted, completed: T.blue,
}[s] || T.textMuted);
const SCHED_STATUS_LABEL = { active: "Active", paused: "Paused", past_due: "Past Due", cancelled: "Cancelled", completed: "Completed" };

export default function BillingHub() {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const { fmtDate, fmtDateTime } = useDateTimeFormat();
  const [tab, setTab] = useState("schedules");
  const [runs, setRuns] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [summary, setSummary] = useState(null);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  // Failed / Transactions tab state
  const [txns, setTxns] = useState([]);
  const [txnStatus, setTxnStatus] = useState("failed");
  const [txnCategory, setTxnCategory] = useState("");
  const [txnRange, setTxnRange] = useState(null);  // { start, end } — set when drilling in from a dated summary tile
  const [txnRunId, setTxnRunId] = useState(null);  // set when drilling in from a specific Run History row
  const [processingId, setProcessingId] = useState(null);

  // Schedules tab state
  const [schedules, setSchedules] = useState([]);
  const [schedCategory, setSchedCategory] = useState("");
  const [schedStatus, setSchedStatus] = useState("");
  const [expandedSched, setExpandedSched] = useState(null); // `${category}-${schedule_id}`
  const [schedHistory, setSchedHistory] = useState(null);
  const [editingSched, setEditingSched] = useState(null); // { category, schedule_id, amount, frequency, next_run_date }
  const [schedBusy, setSchedBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      api.get("/billing-schedule/runs"),
      api.get("/billing-schedule/upcoming", { params: { days: 30 } }),
      api.get("/billing-schedule/needs-attention"),
      api.get("/billing-schedule/summary"),
    ]).then(([r1, r2, r3, r4]) => {
      if (r1.status === "fulfilled") setRuns(r1.value.data.runs || []);
      if (r2.status === "fulfilled") setUpcoming(r2.value.data.upcoming || []);
      if (r3.status === "fulfilled") setNeedsAttention(r3.value.data.needs_attention || []);
      if (r4.status === "fulfilled") setSummary(r4.value.data);
    }).finally(() => setLoading(false));
  }, []);

  const loadTxns = useCallback(() => {
    const params = {};
    if (txnStatus) params.status = txnStatus;
    if (txnCategory) params.category = txnCategory;
    if (txnRunId) {
      params.run_id = txnRunId;
    } else if (txnRange) {
      params.start = txnRange.start; params.end = txnRange.end;
    }
    api.get("/billing-schedule/transactions", { params }).then((res) => setTxns(res.data.transactions || []));
  }, [txnStatus, txnCategory, txnRunId, txnRange]);

  // Drill-down from a Collected/Failed figure — the Overview tiles (dated,
  // no run) and Run History's per-run amounts (a single run, any date) both
  // land here; either way the Transactions tab opens pre-filtered to exactly
  // the rows that made up the number that was clicked.
  const drillIntoTransactions = (status, { runId, range } = {}) => {
    setTxnStatus(status);
    setTxnCategory("");
    setTxnRunId(runId || null);
    setTxnRange(runId ? null : (range || null));
    setTab("transactions");
  };

  const loadSchedules = useCallback(() => {
    const params = {};
    if (schedCategory) params.category = schedCategory;
    if (schedStatus) params.status = schedStatus;
    api.get("/billing-schedule/schedules", { params }).then((res) => setSchedules(res.data.schedules || []));
  }, [schedCategory, schedStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "transactions") loadTxns(); }, [tab, loadTxns]);
  useEffect(() => { if (tab === "schedules") loadSchedules(); }, [tab, loadSchedules]);

  const toggleHistory = (category, scheduleId) => {
    const key = `${category}-${scheduleId}`;
    if (expandedSched === key) { setExpandedSched(null); setSchedHistory(null); return; }
    setExpandedSched(key); setSchedHistory(null);
    api.get(`/billing-schedule/schedules/${category}/${scheduleId}/history`).then((res) => setSchedHistory(res.data));
  };

  const schedAction = (category, scheduleId, action, body) => {
    const key = `${category}-${scheduleId}`;
    setSchedBusy(key); setMsg(null);
    api.post(`/billing-schedule/schedules/${category}/${scheduleId}/${action}`, body || {})
      .then(() => { setMsg({ ok: true, text: `Schedule ${action}d.` }); loadSchedules(); load(); })
      .catch((e) => setMsg({ ok: false, text: e?.response?.data?.detail || `Could not ${action} schedule.` }))
      .finally(() => setSchedBusy(null));
  };

  const saveScheduleEdit = () => {
    const { category, schedule_id, amount, frequency, next_run_date } = editingSched;
    const key = `${category}-${schedule_id}`;
    setSchedBusy(key);
    api.put(`/billing-schedule/schedules/${category}/${schedule_id}`, { amount: parseFloat(amount), frequency, next_run_date })
      .then(() => { setMsg({ ok: true, text: "Schedule updated." }); setEditingSched(null); loadSchedules(); })
      .catch((e) => setMsg({ ok: false, text: e?.response?.data?.detail || "Could not update schedule." }))
      .finally(() => setSchedBusy(null));
  };

  const logSectionRef = useRef(null);

  const viewLog = (runId) => {
    setSelectedRunId(runId);
    api.get(`/billing-schedule/runs/${runId}/log`).then((res) => setSelectedLog(res.data.log || []));
  };

  // Runs after the log detail section has actually committed to the DOM —
  // scheduling the scroll inside the click handler's own promise callback
  // (before React re-renders) would sometimes fire before the ref was attached.
  useEffect(() => {
    if (selectedRunId && selectedLog) {
      logSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedRunId, selectedLog]);

  const reactivate = (category, scheduleId) => {
    api.post(`/billing-schedule/schedules/${category}/${scheduleId}/reactivate`)
      .then(() => { setMsg({ ok: true, text: "Schedule reactivated." }); load(); })
      .catch(() => setMsg({ ok: false, text: "Could not reactivate schedule." }));
  };

  const processNow = (category, scheduleId) => {
    const key = `${category}-${scheduleId}`;
    setProcessingId(key); setMsg(null);
    api.post(`/billing-schedule/schedules/${category}/${scheduleId}/charge-now`)
      .then((res) => {
        const d = res.data;
        setMsg({ ok: d.success > 0, text: d.success > 0 ? "Charge succeeded." : `Charge failed again — check the transaction detail below.` });
        loadTxns(); load();
      })
      .catch((e) => setMsg({ ok: false, text: e?.response?.data?.detail || "Manual charge failed." }))
      .finally(() => setProcessingId(null));
  };

  // Standard rule: every table column is sortable — click to sort, click again to flip.
  const schedSort = useSort(schedules, "next_run_date");
  const runSort = useSort(runs, "run_id", "desc");
  const txnSort = useSort(txns, "executed_at", "desc");
  const upcomingSort = useSort(upcoming, "next_run_date");
  const attentionSort = useSort(needsAttention, "next_run_date");

  return (
    <div style={{ padding: "24px 28px", fontFamily: T.font }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <PageHeaderIcon icon={<FaSyncAlt size={20} color={getBrandColors().blueDark} />} />
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.textPrimary }}>Billing Hub</div>
            <div style={{ fontSize: 12.5, color: T.textMuted }}>Recurring charges for Student Billing and Lane Booking Memberships — billed automatically on each schedule's due date. For a one-off charge outside that schedule, use <strong>Charge Now</strong> on the student's or member's own billing profile.</div>
          </div>
        </div>
      </div>

      {msg && (
        <div style={{ padding: "10px 16px", borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: msg.ok ? "rgba(16,185,129,0.10)" : "rgba(239,68,68,0.10)", color: msg.ok ? T.green : T.red }}>
          {msg.text}
        </div>
      )}

      {/* Summary tiles */}
      {summary && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 22 }}>
          <div style={{ ...tile(T), cursor: "pointer" }}
            title="View the successful transactions behind this figure"
            onClick={() => drillIntoTransactions("success", { range: { start: summary.start, end: summary.end } })}>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Collected (30d)</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.green, textDecoration: "underline dotted" }}>{fmt(summary.totals.total_collected)}</div>
          </div>
          <div style={{ ...tile(T), cursor: "pointer" }}
            title="View the failed transactions behind this figure"
            onClick={() => drillIntoTransactions("failed", { range: { start: summary.start, end: summary.end } })}>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Failed (30d)</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.red, textDecoration: "underline dotted" }}>{fmt(summary.totals.total_failed_amount)}</div>
          </div>
          <div style={tile(T)}>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Success Rate</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.textPrimary }}>
              {summary.totals.total_attempts ? Math.round((summary.totals.success_count / summary.totals.total_attempts) * 100) : 0}%
            </div>
          </div>
          <div style={{ ...tile(T), cursor: needsAttention.length ? "pointer" : "default" }}
            title={needsAttention.length ? "View these schedules" : undefined}
            onClick={() => needsAttention.length && setTab("attention")}>
            <div style={{ fontSize: 11, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Needs Attention</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: needsAttention.length ? T.red : T.textPrimary }}>{needsAttention.length}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        <div onClick={() => setTab("schedules")} style={tabBtn(T, tab === "schedules")}><FaListUl size={12} /> Schedules</div>
        <div onClick={() => setTab("runs")} style={tabBtn(T, tab === "runs")}><FaHistory size={12} /> Run History</div>
        <div onClick={() => setTab("transactions")} style={tabBtn(T, tab === "transactions")}><FaFilter size={12} /> Transactions</div>
        <div onClick={() => setTab("upcoming")} style={tabBtn(T, tab === "upcoming")}><FaCalendarAlt size={12} /> Upcoming ({upcoming.length})</div>
        <div onClick={() => setTab("attention")} style={tabBtn(T, tab === "attention")}><FaExclamationTriangle size={12} /> Needs Attention ({needsAttention.length})</div>
      </div>

      {loading ? (
        <div style={{ ...glassCardSt(T), textAlign: "center", color: T.textMuted }}>Loading…</div>
      ) : (
        <>
          {tab === "schedules" && (
            <div style={glassCardSt(T)}>
              <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                <FaFilter size={12} color={T.textMuted} />
                <select value={schedCategory} onChange={(e) => setSchedCategory(e.target.value)} style={selSt(T)}>
                  <option value="">All categories</option>
                  <option value="student">Student Billing</option>
                  <option value="lane_membership">Lane Membership</option>
                </select>
                <select value={schedStatus} onChange={(e) => setSchedStatus(e.target.value)} style={selSt(T)}>
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="past_due">Past Due</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
                <span style={{ fontSize: 12, color: T.textMuted }}>{schedules.length} schedule{schedules.length === 1 ? "" : "s"}</span>
              </div>
              <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: T.textMuted, fontSize: 10.5, textTransform: "uppercase" }}>
                    <th style={{ padding: "6px", ...schedSort.thSort("category", T) }} onClick={() => schedSort.toggleSort("category")}>Category{schedSort.sortArrow("category")}</th>
                    <th style={{ padding: "6px", ...schedSort.thSort("entity_name", T) }} onClick={() => schedSort.toggleSort("entity_name")}>Entity{schedSort.sortArrow("entity_name")}</th>
                    <th style={{ padding: "6px", ...schedSort.thSort("frequency", T) }} onClick={() => schedSort.toggleSort("frequency")}>Frequency{schedSort.sortArrow("frequency")}</th>
                    <th style={{ padding: "6px", ...schedSort.thSort("amount", T) }} onClick={() => schedSort.toggleSort("amount")}>Amount{schedSort.sortArrow("amount")}</th>
                    <th style={{ padding: "6px", ...schedSort.thSort("next_run_date", T) }} onClick={() => schedSort.toggleSort("next_run_date")}>Next Run{schedSort.sortArrow("next_run_date")}</th>
                    <th style={{ padding: "6px", ...schedSort.thSort("next_retry_date", T) }} onClick={() => schedSort.toggleSort("next_retry_date")}>Retry On{schedSort.sortArrow("next_retry_date")}</th>
                    <th style={{ padding: "6px", ...schedSort.thSort("status", T) }} onClick={() => schedSort.toggleSort("status")}>Status{schedSort.sortArrow("status")}</th>
                    <th style={{ padding: "6px", ...schedSort.thSort("attempt_number", T) }} onClick={() => schedSort.toggleSort("attempt_number")}>Attempts{schedSort.sortArrow("attempt_number")}</th>
                    <th style={{ padding: "6px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {schedSort.sorted.map((s) => {
                    const key = `${s.category}-${s.schedule_id}`;
                    const busy = schedBusy === key;
                    const editing = editingSched && editingSched.category === s.category && editingSched.schedule_id === s.schedule_id;
                    return (
                      <React.Fragment key={key}>
                        <tr style={{ borderTop: `1px solid ${T.divider}` }}>
                          <td style={{ padding: "6px" }}>{CATEGORY_LABEL[s.category] || s.category}</td>
                          <td style={{ padding: "6px" }}>
                            <div style={{ fontWeight: 600 }}>{s.entity_name || `#${s.entity_id}`}</div>
                            {s.program_ref && <div style={{ fontSize: 11, color: T.textMuted }}>{s.program_ref}</div>}
                          </td>
                          <td style={{ padding: "6px" }}>
                            {editing ? (
                              <select value={editingSched.frequency} onChange={(e) => setEditingSched({ ...editingSched, frequency: e.target.value })} style={selSt(T)}>
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="semi_annual">Semi-Annual</option>
                                <option value="annual">Annual</option>
                              </select>
                            ) : <span style={{ textTransform: "capitalize" }}>{s.frequency}</span>}
                          </td>
                          <td style={{ padding: "6px" }}>
                            {editing ? (
                              <input type="number" step="0.01" value={editingSched.amount}
                                onChange={(e) => setEditingSched({ ...editingSched, amount: e.target.value })}
                                style={{ ...selSt(T), width: 80 }} />
                            ) : fmt(s.amount)}
                          </td>
                          <td style={{ padding: "6px" }}>
                            {editing ? (
                              <input type="date" value={editingSched.next_run_date || ""}
                                onChange={(e) => setEditingSched({ ...editingSched, next_run_date: e.target.value })}
                                style={{ ...selSt(T), width: 130 }} />
                            ) : fmtDate(s.next_run_date)}
                          </td>
                          <td style={{ padding: "6px", color: s.next_retry_date ? T.orange : T.textMuted }}>{s.next_retry_date ? fmtDate(s.next_retry_date) : "—"}</td>
                          <td style={{ padding: "6px", color: SCHED_STATUS_COLOR(T, s.status), fontWeight: 700 }}>
                            {SCHED_STATUS_LABEL[s.status] || s.status}
                          </td>
                          <td style={{ padding: "6px" }}>{s.attempt_number || 0}</td>
                          <td style={{ padding: "6px", whiteSpace: "nowrap" }}>
                            {editing ? (
                              <>
                                <button onClick={saveScheduleEdit} disabled={busy} style={{ marginRight: 6, padding: "4px 8px", borderRadius: 6, border: "none", background: T.green, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Save</button>
                                <button onClick={() => setEditingSched(null)} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.inputBdr}`, background: "transparent", color: T.textMuted, fontSize: 11, cursor: "pointer" }}>Cancel</button>
                              </>
                            ) : (
                              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                {s.status === "active" && (
                                  <FaPause title="Pause" size={12} style={{ cursor: "pointer", color: T.orange }}
                                    onClick={() => schedAction(s.category, s.schedule_id, "pause")} />
                                )}
                                {(s.status === "paused" || s.status === "past_due") && (
                                  <FaPlay title="Resume" size={12} style={{ cursor: "pointer", color: T.green }}
                                    onClick={() => schedAction(s.category, s.schedule_id, "resume")} />
                                )}
                                {["active", "paused", "past_due"].includes(s.status) && (
                                  <FaEdit title="Edit" size={12} style={{ cursor: "pointer", color: T.blue }}
                                    onClick={() => setEditingSched({ category: s.category, schedule_id: s.schedule_id, amount: s.amount, frequency: s.frequency, next_run_date: s.next_run_date })} />
                                )}
                                {s.status !== "cancelled" && (
                                  <FaBan title="Cancel" size={12} style={{ cursor: "pointer", color: T.red }}
                                    onClick={() => { const reason = window.prompt("Cancellation reason (optional):") || ""; schedAction(s.category, s.schedule_id, "cancel", { reason }); }} />
                                )}
                                {expandedSched === key ? (
                                  <FaChevronUp title="Hide history" size={12} style={{ cursor: "pointer", color: T.textMuted }} onClick={() => toggleHistory(s.category, s.schedule_id)} />
                                ) : (
                                  <FaChevronDown title="View history" size={12} style={{ cursor: "pointer", color: T.textMuted }} onClick={() => toggleHistory(s.category, s.schedule_id)} />
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                        {expandedSched === key && (
                          <tr>
                            <td colSpan={9} style={{ padding: "10px 6px 16px", background: T.glass }}>
                              {!schedHistory ? (
                                <div style={{ fontSize: 12, color: T.textMuted }}>Loading history…</div>
                              ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                  <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", marginBottom: 6 }}>Billing Cycles</div>
                                    {schedHistory.cycles.length === 0 ? (
                                      <div style={{ fontSize: 12, color: T.textMuted }}>No cycles yet.</div>
                                    ) : schedHistory.cycles.map((c) => (
                                      <div key={c.cycle_id} style={{ fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${T.divider}`, display: "flex", gap: 12 }}>
                                        <span style={{ minWidth: 110, fontWeight: 700 }}>{c.billing_period}</span>
                                        <span style={{ minWidth: 90 }}>{fmt(c.amount)}</span>
                                        <span style={{ color: STATUS_COLOR(T, c.status === "paid" ? "success" : c.status), fontWeight: 700, minWidth: 70 }}>{c.status}</span>
                                        <span style={{ color: T.textMuted }}>{c.attempt_count} attempt{c.attempt_count === 1 ? "" : "s"}</span>
                                        {c.stripe_payment_intent_id && <span style={{ color: T.textMuted, fontFamily: "monospace", fontSize: 11 }}>{c.stripe_payment_intent_id}</span>}
                                      </div>
                                    ))}
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", marginBottom: 6 }}>Audit Trail</div>
                                    {schedHistory.audit.length === 0 ? (
                                      <div style={{ fontSize: 12, color: T.textMuted }}>No changes logged.</div>
                                    ) : schedHistory.audit.map((a) => (
                                      <div key={a.audit_id} style={{ fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${T.divider}` }}>
                                        <span style={{ fontWeight: 700 }}>{a.action}</span>
                                        {a.field_changed && <span style={{ color: T.textMuted }}> — {a.field_changed}: {a.old_value} → {a.new_value}</span>}
                                        <span style={{ color: T.textMuted }}> · {a.performed_by} · {fmtDateTime(a.created_at)}</span>
                                        {a.notes && <span style={{ color: T.textMuted, fontStyle: "italic" }}> ({a.notes})</span>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {schedules.length === 0 && <tr><td colSpan={9} style={{ padding: 16, textAlign: "center", color: T.textMuted }}>No schedules match these filters.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "runs" && (
            <div style={glassCardSt(T)}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: T.textMuted, fontSize: 11, textTransform: "uppercase" }}>
                    <th style={{ padding: "6px 8px", ...runSort.thSort("started_at", T) }} onClick={() => runSort.toggleSort("started_at")}>Started{runSort.sortArrow("started_at")}</th>
                    <th style={{ padding: "6px 8px", ...runSort.thSort("triggered_by", T) }} onClick={() => runSort.toggleSort("triggered_by")}>Triggered By{runSort.sortArrow("triggered_by")}</th>
                    <th style={{ padding: "6px 8px", ...runSort.thSort("due_count", T) }} onClick={() => runSort.toggleSort("due_count")}>Due{runSort.sortArrow("due_count")}</th>
                    <th style={{ padding: "6px 8px", ...runSort.thSort("success_count", T) }} onClick={() => runSort.toggleSort("success_count")}>Success{runSort.sortArrow("success_count")}</th>
                    <th style={{ padding: "6px 8px", ...runSort.thSort("failed_count", T) }} onClick={() => runSort.toggleSort("failed_count")}>Failed{runSort.sortArrow("failed_count")}</th>
                    <th style={{ padding: "6px 8px", ...runSort.thSort("skipped_count", T) }} onClick={() => runSort.toggleSort("skipped_count")}>Skipped{runSort.sortArrow("skipped_count")}</th>
                    <th style={{ padding: "6px 8px", ...runSort.thSort("collected_amount", T) }} onClick={() => runSort.toggleSort("collected_amount")}>Collected{runSort.sortArrow("collected_amount")}</th>
                    <th style={{ padding: "6px 8px", ...runSort.thSort("failed_amount", T) }} onClick={() => runSort.toggleSort("failed_amount")}>Failed Amt{runSort.sortArrow("failed_amount")}</th>
                    <th style={{ padding: "6px 8px", ...runSort.thSort("status", T) }} onClick={() => runSort.toggleSort("status")}>Status{runSort.sortArrow("status")}</th>
                    <th style={{ padding: "6px 8px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {runSort.sorted.map((r) => (
                    <tr key={r.run_id} style={{ borderTop: `1px solid ${T.divider}` }}>
                      <td style={{ padding: "8px" }}>{fmtDateTime(r.started_at)}</td>
                      <td style={{ padding: "8px" }}>{r.triggered_by}</td>
                      <td style={{ padding: "8px" }}>{r.due_count}</td>
                      <td style={{ padding: "8px", color: T.green }}>{r.success_count}</td>
                      <td style={{ padding: "8px", color: r.failed_count ? T.red : T.textMuted }}>{r.failed_count}</td>
                      <td style={{ padding: "8px" }}>{r.skipped_count}</td>
                      <td style={{ padding: "8px" }}>
                        {Number(r.collected_amount) > 0 ? (
                          <span onClick={() => drillIntoTransactions("success", { runId: r.run_id })}
                            title="View the successful transactions behind this figure"
                            style={{ cursor: "pointer", color: T.green, fontWeight: 700, textDecoration: "underline dotted" }}>
                            {fmt(r.collected_amount)}
                          </span>
                        ) : <span style={{ color: T.textMuted }}>{fmt(0)}</span>}
                      </td>
                      <td style={{ padding: "8px" }}>
                        {Number(r.failed_amount) > 0 ? (
                          <span onClick={() => drillIntoTransactions("failed", { runId: r.run_id })}
                            title="View the failed transactions behind this figure"
                            style={{ cursor: "pointer", color: T.red, fontWeight: 700, textDecoration: "underline dotted" }}>
                            {fmt(r.failed_amount)}
                          </span>
                        ) : <span style={{ color: T.textMuted }}>{fmt(0)}</span>}
                      </td>
                      <td style={{ padding: "8px" }}>{r.status}</td>
                      <td style={{ padding: "8px" }}>
                        <span onClick={() => viewLog(r.run_id)} style={{ cursor: "pointer", color: T.blue, fontWeight: 700 }}>View log</span>
                      </td>
                    </tr>
                  ))}
                  {runs.length === 0 && <tr><td colSpan={10} style={{ padding: 16, textAlign: "center", color: T.textMuted }}>No runs yet.</td></tr>}
                </tbody>
              </table>

              {selectedRunId && selectedLog && (
                <div ref={logSectionRef} style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.divider}`, scrollMarginTop: 16 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Run #{selectedRunId} — attempt detail</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {/* A fixed-width grid (not flexbox) so every field lines up into a true
                        column across rows — flexbox with content-sized items let a long
                        name/amount in one row push everything after it out of alignment
                        with the row above/below. */}
                    <div style={{ display: "grid", gridTemplateColumns: "110px 5px 190px 5px 110px 5px 150px 5px 90px 1fr 110px",
                      fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.textMuted,
                      padding: "0 20px 8px" }}>
                      <div>Category</div><div /><div>Name</div><div /><div>Billing Period</div><div /><div>Run Date</div><div /><div>Amount</div><div /><div>Status</div>
                    </div>
                    {selectedLog.map((l) => (
                      <div key={l.log_id} style={{ ...glassCardSt(T), padding: "14px 20px" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "110px 5px 190px 5px 110px 5px 150px 5px 90px 1fr 110px", alignItems: "center" }}>
                          <div style={{ fontSize: 13.5 }}>{CATEGORY_LABEL[l.category] || l.category}</div>
                          <div />
                          <div style={{ fontSize: 13.5 }}>{l.entity_name} <span style={{ color: T.textMuted }}>(#{l.entity_id})</span></div>
                          <div />
                          <div style={{ fontSize: 13.5 }}>{l.billing_period || "—"}</div>
                          <div />
                          <div style={{ fontSize: 13.5 }}>{fmtDateTime(l.executed_at)}</div>
                          <div />
                          <div style={{ fontSize: 13.5, fontWeight: 700 }}>{fmt(l.amount)}</div>
                          <div />
                          <div style={{ display: "flex", alignItems: "center", gap: 6, color: STATUS_COLOR(T, l.status), fontWeight: 700, fontSize: 13.5 }}>
                            {l.status === "success" ? <FaCheckCircle size={13} /> : l.status === "failed" ? <FaTimesCircle size={13} /> : null} {l.status}
                          </div>
                        </div>
                        {l.error_message && (
                          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.divider}`, fontSize: 12.5, color: T.textMuted, lineHeight: 1.6 }}>
                            {l.error_message}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "transactions" && (
            <div style={glassCardSt(T)}>
              <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                <FaFilter size={12} color={T.textMuted} />
                <select value={txnStatus} onChange={(e) => { setTxnStatus(e.target.value); setTxnRunId(null); setTxnRange(null); }} style={selSt(T)}>
                  <option value="">All statuses</option>
                  <option value="failed">Failed</option>
                  <option value="success">Success</option>
                  <option value="skipped_no_card">Skipped — no card</option>
                  <option value="skipped_already_run">Skipped — already run</option>
                </select>
                <select value={txnCategory} onChange={(e) => { setTxnCategory(e.target.value); setTxnRunId(null); setTxnRange(null); }} style={selSt(T)}>
                  <option value="">All categories</option>
                  <option value="student">Student Billing</option>
                  <option value="lane_membership">Lane Membership</option>
                </select>
                {(txnRunId || txnRange) && (
                  <span onClick={() => { setTxnRunId(null); setTxnRange(null); }}
                    style={{ fontSize: 11, fontWeight: 700, color: T.blue, cursor: "pointer", padding: "4px 10px", borderRadius: 20, background: T.glass, border: `1px solid ${T.inputBdr}` }}>
                    {txnRunId ? `Filtered to Run #${txnRunId}` : "Filtered to dated range"} ✕
                  </span>
                )}
                <span style={{ fontSize: 12, color: T.textMuted }}>
                  {txns.length} result{txns.length === 1 ? "" : "s"} {!txnRunId && `(${txnRange ? `${fmtDate(txnRange.start)} → ${fmtDate(txnRange.end)}` : "last 90 days"})`}
                </span>
              </div>
              <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: T.textMuted, fontSize: 10.5, textTransform: "uppercase" }}>
                    <th style={{ padding: "6px", ...txnSort.thSort("category", T) }} onClick={() => txnSort.toggleSort("category")}>Category{txnSort.sortArrow("category")}</th>
                    <th style={{ padding: "6px", ...txnSort.thSort("entity_name", T) }} onClick={() => txnSort.toggleSort("entity_name")}>Entity{txnSort.sortArrow("entity_name")}</th>
                    <th style={{ padding: "6px", ...txnSort.thSort("billing_period", T) }} onClick={() => txnSort.toggleSort("billing_period")}>Billing Period{txnSort.sortArrow("billing_period")}</th>
                    <th style={{ padding: "6px", ...txnSort.thSort("executed_at", T) }} onClick={() => txnSort.toggleSort("executed_at")}>Run Date{txnSort.sortArrow("executed_at")}</th>
                    <th style={{ padding: "6px", ...txnSort.thSort("amount", T) }} onClick={() => txnSort.toggleSort("amount")}>Amount{txnSort.sortArrow("amount")}</th>
                    <th style={{ padding: "6px", ...txnSort.thSort("attempt_number", T) }} onClick={() => txnSort.toggleSort("attempt_number")}>Attempt{txnSort.sortArrow("attempt_number")}</th>
                    <th style={{ padding: "6px", ...txnSort.thSort("status", T) }} onClick={() => txnSort.toggleSort("status")}>Status{txnSort.sortArrow("status")}</th>
                    <th style={{ padding: "6px" }}>Detail</th>
                    <th style={{ padding: "6px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {txnSort.sorted.map((t) => {
                    const key = `${t.category}-${t.schedule_id}`;
                    return (
                      <tr key={t.log_id} style={{ borderTop: `1px solid ${T.divider}` }}>
                        <td style={{ padding: "6px" }}>{CATEGORY_LABEL[t.category] || t.category}</td>
                        <td style={{ padding: "6px" }}>{t.entity_name} <span style={{ color: T.textMuted }}>(#{t.entity_id})</span></td>
                        <td style={{ padding: "6px" }}>{t.billing_period || "—"}</td>
                        <td style={{ padding: "6px" }}>{fmtDateTime(t.executed_at)}</td>
                        <td style={{ padding: "6px" }}>{fmt(t.amount)}</td>
                        <td style={{ padding: "6px" }}>{t.attempt_number}</td>
                        <td style={{ padding: "6px", color: STATUS_COLOR(T, t.status), fontWeight: 700 }}>{t.status}</td>
                        <td style={{ padding: "6px", color: T.textMuted, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>{t.error_message || "—"}</td>
                        <td style={{ padding: "6px" }}>
                          {t.status === "failed" && (
                            <button onClick={() => processNow(t.category, t.schedule_id)} disabled={processingId === key}
                              style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 8,
                                border: "none", cursor: processingId === key ? "not-allowed" : "pointer",
                                background: T.red, color: "#fff", fontSize: 11, fontWeight: 700, opacity: processingId === key ? 0.6 : 1 }}>
                              <FaRedo size={9} /> {processingId === key ? "Processing…" : "Process Now"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {txns.length === 0 && <tr><td colSpan={9} style={{ padding: 16, textAlign: "center", color: T.textMuted }}>No matching transactions.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "upcoming" && (
            <div style={glassCardSt(T)}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: T.textMuted, fontSize: 11, textTransform: "uppercase" }}>
                    <th style={{ padding: "6px 8px", ...upcomingSort.thSort("next_run_date", T) }} onClick={() => upcomingSort.toggleSort("next_run_date")}>Due Date{upcomingSort.sortArrow("next_run_date")}</th>
                    <th style={{ padding: "6px 8px", ...upcomingSort.thSort("category", T) }} onClick={() => upcomingSort.toggleSort("category")}>Category{upcomingSort.sortArrow("category")}</th>
                    <th style={{ padding: "6px 8px", ...upcomingSort.thSort("entity_id", T) }} onClick={() => upcomingSort.toggleSort("entity_id")}>Entity ID{upcomingSort.sortArrow("entity_id")}</th>
                    <th style={{ padding: "6px 8px", ...upcomingSort.thSort("frequency", T) }} onClick={() => upcomingSort.toggleSort("frequency")}>Frequency{upcomingSort.sortArrow("frequency")}</th>
                    <th style={{ padding: "6px 8px", ...upcomingSort.thSort("amount", T) }} onClick={() => upcomingSort.toggleSort("amount")}>Amount{upcomingSort.sortArrow("amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingSort.sorted.map((u) => (
                    <tr key={`${u.category}-${u.schedule_id}`} style={{ borderTop: `1px solid ${T.divider}` }}>
                      <td style={{ padding: "8px" }}>{fmtDate(u.next_run_date)}</td>
                      <td style={{ padding: "8px" }}>{CATEGORY_LABEL[u.category] || u.category}</td>
                      <td style={{ padding: "8px" }}>#{u.entity_id}</td>
                      <td style={{ padding: "8px", textTransform: "capitalize" }}>{u.frequency}</td>
                      <td style={{ padding: "8px" }}>{fmt(u.amount)}</td>
                    </tr>
                  ))}
                  {upcoming.length === 0 && <tr><td colSpan={5} style={{ padding: 16, textAlign: "center", color: T.textMuted }}>Nothing due in this window.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === "attention" && (
            <div style={glassCardSt(T)}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", color: T.textMuted, fontSize: 11, textTransform: "uppercase" }}>
                    <th style={{ padding: "6px 8px", ...attentionSort.thSort("category", T) }} onClick={() => attentionSort.toggleSort("category")}>Category{attentionSort.sortArrow("category")}</th>
                    <th style={{ padding: "6px 8px", ...attentionSort.thSort("entity_id", T) }} onClick={() => attentionSort.toggleSort("entity_id")}>Entity ID{attentionSort.sortArrow("entity_id")}</th>
                    <th style={{ padding: "6px 8px", ...attentionSort.thSort("amount", T) }} onClick={() => attentionSort.toggleSort("amount")}>Amount{attentionSort.sortArrow("amount")}</th>
                    <th style={{ padding: "6px 8px", ...attentionSort.thSort("attempt_number", T) }} onClick={() => attentionSort.toggleSort("attempt_number")}>Failed Attempts{attentionSort.sortArrow("attempt_number")}</th>
                    <th style={{ padding: "6px 8px", ...attentionSort.thSort("next_run_date", T) }} onClick={() => attentionSort.toggleSort("next_run_date")}>Stuck Since{attentionSort.sortArrow("next_run_date")}</th>
                    <th style={{ padding: "6px 8px" }}></th>
                    <th style={{ padding: "6px 8px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {attentionSort.sorted.map((n) => {
                    const key = `${n.category}-${n.schedule_id}`;
                    return (
                      <tr key={key} style={{ borderTop: `1px solid ${T.divider}` }}>
                        <td style={{ padding: "8px" }}>{CATEGORY_LABEL[n.category] || n.category}</td>
                        <td style={{ padding: "8px" }}>#{n.entity_id}</td>
                        <td style={{ padding: "8px" }}>{fmt(n.amount)}</td>
                        <td style={{ padding: "8px", color: T.red, fontWeight: 700 }}>{n.attempt_number}</td>
                        <td style={{ padding: "8px" }}>{fmtDate(n.next_run_date)}</td>
                        <td style={{ padding: "8px" }}>
                          <span onClick={() => processNow(n.category, n.schedule_id)} style={{ cursor: processingId === key ? "not-allowed" : "pointer", color: T.red, fontWeight: 700, opacity: processingId === key ? 0.6 : 1 }}>
                            {processingId === key ? "Processing…" : "Process Now"}
                          </span>
                        </td>
                        <td style={{ padding: "8px" }}>
                          <span onClick={() => reactivate(n.category, n.schedule_id)} style={{ cursor: "pointer", color: T.blue, fontWeight: 700 }}>Reactivate</span>
                        </td>
                      </tr>
                    );
                  })}
                  {needsAttention.length === 0 && <tr><td colSpan={7} style={{ padding: 16, textAlign: "center", color: T.textMuted }}>Nothing needs attention.</td></tr>}
                </tbody>
              </table>

              {summary && summary.failure_breakdown.length > 0 && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.divider}` }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, marginBottom: 10 }}>
                    Why charges are failing ({fmtDate(summary.start)} → {fmtDate(summary.end)})
                  </div>
                  <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                    <tbody>
                      {summary.failure_breakdown.map((f, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${T.divider}` }}>
                          <td style={{ padding: "8px 0", color: T.textPrimary }}>{f.error_message}</td>
                          <td style={{ padding: "8px 0", textAlign: "right", color: T.red, fontWeight: 700 }}>{f.n}×</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
